/**
 * Live-cached Workspace field statuses (frontend-only).
 *
 * observed      — value taken from the payload as a market/source reading
 *                 (never used for client-joined or client-computed values)
 * derived       — computed in this adapter from other payload fields
 * cached        — present on the house Mongo-first GET payload
 * partial       — some required pieces present, others missing
 * unavailable   — field absent from the current payload
 * not_evaluated — intentionally not computed / not requested in this phase
 *
 * Fetch time is never treated as market observation time.
 * Client-derived values are never labelled observed.
 */

export const FIELD_STATUS = {
  observed: "observed",
  derived: "derived",
  cached: "cached",
  partial: "partial",
  unavailable: "unavailable",
  not_evaluated: "not_evaluated",
};

export function liveField(value, status, extras = {}) {
  return {
    value: value === undefined ? null : value,
    status,
    asOf: extras.asOf ?? null,
    fetchedAt: extras.fetchedAt ?? null,
    sourceCategory: extras.sourceCategory || "house_cache",
    path: extras.path || "",
    label: extras.label || "",
  };
}

export const UNAVAILABLE_COPY = {
  alignment: "Historical regime alignment unavailable",
  notInCache: "Not available from current cached data",
  notInOutput: "Not available in current cached output",
  notYetCached: "Not yet available from current cached data",
  timestamp: "timestamp unavailable",
  indicative: "indicative estimate only",
  historicalAvailable: "historical context available",
  historicalLimited: "historical context limited",
  historicalUnavailable: "historical context unavailable",
};

/** Plan A view-model enums. Display only — do not invent quote times or premiums. */
export const HISTORICAL_CONTEXT_KIND = {
  historical_backtest_not_current: "historical_backtest_not_current",
  historical_limited: "historical_limited",
  historical_unavailable: "historical_unavailable",
};

export const CURRENT_QUOTE_AVAILABILITY = {
  no_chain: "no_chain",
  unverified_timestamp: "unverified_timestamp",
  freshness_blocked: "freshness_blocked",
  no_selected_structure: "no_selected_structure",
  premium_unavailable: "premium_unavailable",
  verified_observed: "verified_observed",
};

/** Deterministic primary current-quote reason. First match wins. */
export const CURRENT_QUOTE_PRECEDENCE = [
  CURRENT_QUOTE_AVAILABILITY.no_chain,
  CURRENT_QUOTE_AVAILABILITY.unverified_timestamp,
  CURRENT_QUOTE_AVAILABILITY.freshness_blocked,
  CURRENT_QUOTE_AVAILABILITY.no_selected_structure,
  CURRENT_QUOTE_AVAILABILITY.premium_unavailable,
  CURRENT_QUOTE_AVAILABILITY.verified_observed,
];

export const CONTEXT_KIND = {
  historical_backtest: "historical_backtest",
  current_quote: "current_quote",
};

/** Plan A.1 research-focus states. Identity comes only from ranked_strategies[]. */
export const RESEARCH_FOCUS_KIND = {
  choose: "choose",
  pending: "pending",
  unavailable: "unavailable",
  ready: "ready",
  limited: "limited",
};

export const COMPLETED_MULTILEG_PRICING_METHODS = [
  "midpoint",
  "natural_credit",
  "natural_debit",
  "last_estimate",
];

/** Static mapping catalogue for the P1 proof report. */
export const WORKSPACE_FIELD_CATALOGUE = [
  { field: "header.symbol", endpoint: "GET /api/ticker/{symbol}/data", path: "ticker", status: "cached" },
  { field: "header.last", endpoint: "GET /api/ticker/{symbol}/data", path: "last", status: "cached" },
  { field: "header.change / changePct", endpoint: "GET /api/ticker/{symbol}/data", path: "ohlcv[-1].close vs prior close", status: "derived" },
  { field: "header.sector", endpoint: "GET /api/ticker/{symbol}/data", path: "sector", status: "cached" },
  { field: "header.company / industry", endpoint: "GET /api/ticker/{symbol}/data", path: "(absent unless payload has company/industry)", status: "unavailable" },
  { field: "header.technicalScore", endpoint: "GET /api/ticker/{symbol}/data", path: "technical_score", status: "cached" },
  { field: "header.fundamentalScore", endpoint: "GET /api/ticker/{symbol}/data", path: "fundamental_score", status: "cached" },
  { field: "regime.instant", endpoint: "GET /api/ticker/{symbol}/data", path: "regime_now / hmm.state_label", status: "cached" },
  { field: "regime.confirmed", endpoint: "GET /api/ticker/{symbol}/data", path: "regime_confirmed / hmm.state_5d", status: "cached" },
  { field: "regime.confidence / sojourn / age / p75", endpoint: "GET /api/ticker/{symbol}/data", path: "hmm.*", status: "cached" },
  { field: "earnings.date / status", endpoint: "GET /api/ticker/{symbol}/data", path: "earnings.date, earnings.source", status: "cached" },
  { field: "options.freshness / pricingMethod / asOf", endpoint: "GET /api/ticker/{symbol}/data", path: "option_freshness, pricing_method, option_asof", status: "cached" },
  { field: "overview.ohlcv", endpoint: "GET /api/ticker/{symbol}/data", path: "ohlcv[]", status: "cached" },
  { field: "overview.sma50 / sma200 series", endpoint: "GET /api/ticker/{symbol}/data", path: "ohlcv[].sma50/sma200 (if present)", status: "unavailable" },
  { field: "overview.regime ribbon", endpoint: "GET /api/regime/{symbol}/history", path: "history[].state_5d | state_label joined on exact date", status: "derived" },
  { field: "overview.trend tape", endpoint: "GET /api/ticker/{symbol}/data", path: "per-bar trend (absent)", status: "unavailable" },
  { field: "technicals.rsi / macd / adx / bb / sr", endpoint: "GET /api/ticker/{symbol}/data", path: "signals.* / support / resistance", status: "cached" },
  { field: "technicals.ema9 / ema20 / weekly ema / atr / rs", endpoint: "(not in current payload)", path: "—", status: "unavailable" },
  { field: "options.expirations / dte", endpoint: "GET /api/ticker/{symbol}/data", path: "available_expirations[]", status: "cached" },
  { field: "options.readiness / basket boolean", endpoint: "GET /api/ticker/{symbol}/data", path: "option_readiness.ready", status: "cached" },
  { field: "options.selected structure", endpoint: "GET /api/ticker/{symbol}/data", path: "legs[] / premium when present", status: "partial" },
  { field: "options.wing_scenarios", endpoint: "GET /api/ticker/{symbol}/data", path: "wing_scenarios (display-only if present)", status: "cached" },
  { field: "options.backtest scalars", endpoint: "GET /api/ticker/{symbol}/data", path: "backtest.*", status: "cached" },
  { field: "options.regime-conditioned table", endpoint: "GET /api/ticker/{symbol}/regime-backtest-context", path: "by_regime[]", status: "cached" },
  { field: "options.strategy-family context", endpoint: "GET /api/regime/ranked-strategies/{symbol}", path: "rankings[] (family context only)", status: "cached" },
  { field: "options.per-trade outcomes chart", endpoint: "(not requested / not in payload)", path: "—", status: "unavailable" },
  { field: "talk.headlines", endpoint: "GET /api/ticker-talk/{ticker}", path: "items[]", status: "cached" },
  { field: "ownership.form4 / 13f / ptr / 144", endpoint: "GET /api/shadow-money/{ticker}", path: "insider / institutional / congress / form144", status: "cached" },
  { field: "fundamentals.profile / statements / peers", endpoint: "(not in current payload)", path: "—", status: "unavailable" },
  { field: "earnings.estimates / surprises / print-move", endpoint: "(not in current payload)", path: "—", status: "unavailable" },
  { field: "recommended_strategy", endpoint: "GET /api/ticker/{symbol}/data", path: "intentionally unused", status: "not_evaluated" },
];
